import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Tooltip } from "@mui/material";
import { Zap, ChevronDown } from "lucide-react";
import { getEndpointLabel } from "../utils/endpointLabel";

const GenerateTestCasesModal = ({
  open,
  onClose,
  collection,
  onConfirm,
  onConfirmChained,
  isLoading = false,
  isLoadingChained = false,
}) => {
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);
  const [generationMode, setGenerationMode] = useState("all");
  const [expandedEndpoints, setExpandedEndpoints] = useState({});
  const [numberOfTestCases, setNumberOfTestCases] = useState(5);
  const [activeTab, setActiveTab] = useState("regular");

  const handleEndpointSelectionChange = (endpointId, isSelected) => {
    if (isSelected) {
      setSelectedEndpoints((prev) => [...prev, endpointId]);
    } else {
      setSelectedEndpoints((prev) => prev.filter((id) => id !== endpointId));
    }
  };

  const handleEndpointToggle = (endpointId) => {
    setExpandedEndpoints((prev) => ({
      ...prev,
      [endpointId]: !prev[endpointId],
    }));
  };

  const handleConfirm = () => {
    if (generationMode === "selected" && selectedEndpoints.length === 0) {
      return;
    }

    if (activeTab === "chained") {
      // Handle chained test cases
      const chainedData = {
        collectionId: generationMode === "all" ? [collection.id] : undefined,
        endpointIds: generationMode === "selected" ? selectedEndpoints : undefined,
      };
      onConfirmChained(chainedData);
    } else {
      // Handle regular test cases
      let endpointsData = null;
      if (generationMode === "selected" && selectedEndpoints.length > 0) {
        endpointsData = collection.endpoints
          .filter((endpoint) => selectedEndpoints.includes(endpoint.id))
          .map((endpoint) => ({
            id: endpoint.id,
            method: endpoint.method,
            path: endpoint.path,
          }));
      }
      onConfirm({
        collectionId: collection.id,
        endpointsData: endpointsData,
        numberOfTestCases: numberOfTestCases === null ? "default" : numberOfTestCases,
      });
    }
  };

  const handleClose = () => {
    setSelectedEndpoints([]);
    setGenerationMode("all");
    setExpandedEndpoints({});
    setNumberOfTestCases(5);
    setActiveTab("regular");
    onClose();
  };

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case "GET":
        return {
          textColor: "text-green-700",
          bgColor: "#f0f9ff",
          borderColor: "#bae6fd",
        };
      case "POST":
        return {
          textColor: "text-blue-700",
          bgColor: "#eff6ff",
          borderColor: "#dbeafe",
        };
      case "PUT":
        return {
          textColor: "text-orange-700",
          bgColor: "#fff7ed",
          borderColor: "#fed7aa",
        };
      case "DELETE":
        return {
          textColor: "text-red-700",
          bgColor: "#fef2f2",
          borderColor: "#fecaca",
        };
      case "PATCH":
        return {
          textColor: "text-purple-700",
          bgColor: "#faf5ff",
          borderColor: "#e9d5ff",
        };
      default:
        return {
          textColor: "text-gray-700",
          bgColor: "#f9fafb",
          borderColor: "#d1d5db",
        };
    }
  };

  const renderEndpointSelection = () => (
    <div>
      <h3 className="text-base font-semibold text-gray-800 mb-4">Select Endpoints</h3>

      {collection?.endpoints?.length === 0 ? (
        <div className="text-center py-8">
          <Zap size={48} className="text-gray-300 mx-auto mb-4" />
          <div className="text-sm text-gray-500">No endpoints found in this collection</div>
        </div>
      ) : (
        <div className="space-y-3">
          {collection.endpoints.map((endpoint) => {
            const isOpen = expandedEndpoints[endpoint.id];
            const methodStyles = getMethodColor(endpoint.method);
            const isSelected = selectedEndpoints.includes(endpoint.id);

            return (
              <div
                key={endpoint.id}
                className={`bg-white border rounded-md shadow-sm transition-all duration-200 ${
                  isSelected
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center justify-between px-4 py-3 cursor-pointer transition-all duration-200 hover:bg-[rgba(147,51,234,0.08)] border-b border-gray-100">
                  <div
                    className="flex items-center flex-1"
                    onClick={() => handleEndpointToggle(endpoint.id)}
                  >
                    <div className="flex items-center justify-center w-[18px] mr-3">
                      <ChevronDown
                        size={16}
                        className={`text-gray-500 transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                    <div className="flex items-center justify-center w-[18px] mr-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleEndpointSelectionChange(endpoint.id, e.target.checked);
                        }}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span
                          className={`text-xs px-2 py-1 rounded font-medium border ${methodStyles.textColor}`}
                          style={{
                            backgroundColor: methodStyles.bgColor,
                            borderColor: methodStyles.borderColor,
                          }}
                        >
                          {endpoint.method}
                        </span>
                        <span className="text-sm text-gray-800 font-mono">{endpoint.path}</span>
                        {getEndpointLabel(endpoint) !== endpoint.path && (
                          <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {getEndpointLabel(endpoint)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {isOpen && (
                  <div className="bg-white">
                    <div className="px-4 py-3 border-t border-gray-100">
                      <div className="space-y-2">
                        <div className="text-sm text-gray-700">
                          <strong>Method:</strong> {endpoint.method}
                        </div>
                        <div className="text-sm text-gray-700">
                          <strong>Path:</strong> <span className="font-mono">{endpoint.path}</span>
                        </div>
                        {getEndpointLabel(endpoint) !== endpoint.path && (
                          <div className="text-sm text-gray-700">
                            <strong>Name:</strong> {getEndpointLabel(endpoint)}
                          </div>
                        )}
                        {endpoint.description && (
                          <div className="text-sm text-gray-700">
                            <strong>Description:</strong> {endpoint.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedEndpoints.length > 0 && (
        <div className="mt-4 p-3 bg-purple-50 rounded border border-purple-200">
          <div className="text-sm text-purple-800">
            <strong>✓ {selectedEndpoints.length} endpoint(s) selected</strong> for test case
            generation
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span className="font-apple text-lg text-gray-800 flex items-center flex-shrink-0">
          Generate Test Cases
          <Zap className="ml-2" size={20} />
        </span>
        <Tooltip title={collection?.name || ""} arrow>
          <span
            className="text-sm text-gray-600 truncate ml-4"
            style={{ display: "inline-block", maxWidth: "50%" }}
          >
            Collection: <strong>{collection?.name}</strong>
          </span>
        </Tooltip>
      </DialogTitle>

      {/* Tab Navigation */}
      <div style={{ borderBottom: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
        <div className="flex">
          <button
            onClick={() => {
              setActiveTab("regular");
              setSelectedEndpoints([]);
              setGenerationMode("all");
            }}
            className={`px-6 py-3 text-sm font-medium transition-all cursor-pointer ${
              activeTab === "regular"
                ? "text-purple-600 border-b-2 border-purple-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <Zap className="inline mr-2" size={16} />
            Regular Test Cases
          </button>
          <button
            onClick={() => {
              setActiveTab("chained");
              setSelectedEndpoints([]);
              setGenerationMode("all");
            }}
            className={`px-6 py-3 text-sm font-medium transition-all cursor-pointer ${
              activeTab === "chained"
                ? "text-purple-600 border-b-2 border-purple-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <span className="inline mr-2">🔗</span>
            Chained Test Cases
          </button>
        </div>
      </div>

      <DialogContent
        sx={{
          padding: "0",
          backgroundColor: "#ffffff",
          overflowY: "auto",
          maxHeight: "60vh",
        }}
      >
        <div className="font-apple p-4">
          {activeTab === "regular" ? (
            <>
              {/* Number of Test Cases Input */}
              <div className="mb-6">
                <h3 className="text-base font-semibold text-gray-800 mb-4">
                  Test Case Configuration
                </h3>

                <div
                  className={`bg-white border rounded-md shadow-sm cursor-pointer transition-all duration-200 mb-3 ${
                    numberOfTestCases !== null
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setNumberOfTestCases(5)}
                >
                  <div className="p-4">
                    <div className="flex items-start">
                      <input
                        type="radio"
                        checked={numberOfTestCases !== null}
                        onChange={() => {}}
                        className="mr-3 mt-1"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-800">
                          Specify Number of Test Cases
                        </div>
                        <div className="text-xs text-gray-600 mt-1 mb-3">
                          Enter the exact number of test cases you want to generate per endpoint
                        </div>
                        {numberOfTestCases !== null && (
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={numberOfTestCases}
                            onChange={(e) => setNumberOfTestCases(parseInt(e.target.value) || 1)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Enter number of test cases"
                            disabled={isLoading}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`bg-white border rounded-md shadow-sm cursor-pointer transition-all duration-200 ${
                    numberOfTestCases === null
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setNumberOfTestCases(null)}
                >
                  <div className="p-4">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        checked={numberOfTestCases === null}
                        onChange={() => {}}
                        className="mr-3"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-800">
                          Default Mode (AI Decides)
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Let the AI model decide the optimal number of test cases based on endpoint
                          complexity
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-base font-semibold text-gray-800 mb-4">
                  Choose Generation Mode
                </h3>

                <div
                  className={`bg-white border rounded-md shadow-sm cursor-pointer transition-all duration-200 mb-3 ${
                    generationMode === "all"
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => {
                    setGenerationMode("all");
                    setSelectedEndpoints([]);
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        checked={generationMode === "all"}
                        onChange={() => {}}
                        className="mr-3"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-800">
                          Generate for All Endpoints
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Create test cases for all {collection?.endpoints?.length || 0} endpoints
                          in this collection
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`bg-white border rounded-md shadow-sm cursor-pointer transition-all duration-200 ${
                    generationMode === "selected"
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setGenerationMode("selected")}
                >
                  <div className="p-4">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        checked={generationMode === "selected"}
                        onChange={() => {}}
                        className="mr-3"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-800">
                          Select Specific Endpoints
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Choose which endpoints to generate test cases for
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {generationMode === "selected" && renderEndpointSelection()}
            </>
          ) : (
            <>
              {/* Chained Test Cases Tab */}
              <div className="mb-6">
                <h3 className="text-base font-semibold text-gray-800 mb-4">
                  Choose Generation Mode
                </h3>

                <div
                  className={`bg-white border rounded-md shadow-sm cursor-pointer transition-all duration-200 mb-3 ${
                    generationMode === "all"
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => {
                    setGenerationMode("all");
                    setSelectedEndpoints([]);
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        checked={generationMode === "all"}
                        onChange={() => {}}
                        className="mr-3"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-800">
                          Generate for Whole Collection
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Create chained test cases for all {collection?.endpoints?.length || 0}{" "}
                          endpoints in this collection
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`bg-white border rounded-md shadow-sm cursor-pointer transition-all duration-200 ${
                    generationMode === "selected"
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setGenerationMode("selected")}
                >
                  <div className="p-4">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        checked={generationMode === "selected"}
                        onChange={() => {}}
                        className="mr-3"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-800">
                          Select Specific Endpoints
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Choose which endpoints to generate chained test cases for
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {generationMode === "selected" && renderEndpointSelection()}
            </>
          )}
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          gap: "12px",
        }}
      >
        <button
          onClick={handleClose}
          disabled={isLoading || isLoadingChained}
          className="px-4 py-2 font-apple text-sm text-gray-600 border border-gray-300 hover:bg-gray-50 transition-all duration-200 cursor-pointer disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={
            isLoading ||
            isLoadingChained ||
            (generationMode === "selected" && selectedEndpoints.length === 0)
          }
          className="px-4 py-2 font-apple text-sm text-white bg-purple-600 hover:bg-purple-700 transition-all duration-200 cursor-pointer disabled:opacity-50 flex items-center gap-2"
        >
          <Zap size={14} />
          {isLoading || isLoadingChained
            ? "Generating..."
            : activeTab === "chained"
              ? "Generate Chained Test Cases"
              : "Generate Test Cases"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateTestCasesModal;
